import {useEffect, useRef} from "react";
import {Icon} from "@blueprintjs/core";
import {renderToStaticMarkup} from "react-dom/server";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../../common/libs/L.TileLayer.NoGap";
import {blueColor, primaryColor} from "../../common/libs/theme-lib";
import "./transport-map.css";

const KNOWN_STATUSES = ["0", "1", "2", "3", "4"];

const statusLabel = (status, t) =>
    KNOWN_STATUSES.includes(String(status))
        ? t(`statuses.${status}`)
        : t("statuses.other");

const TransportMap = ({transport, t, onClose, client}) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);
    const truck = validPoint(
        transport.latitude,
        transport.longitude
    );
    const destination = validPoint(
        transport.dest_latitude,
        transport.dest_longitude
    );
    const origin = validPoint(
        transport.origin_latitude,
        transport.origin_longitude
    );
    const isInTransit = ["1", "2", "3"].includes(
        String(transport.transport_status)
    );
    const hasArrived =
        String(transport.transport_status) === "4";
    const hasNotStarted =
        String(transport.transport_status) === "0";
    const address = [
        transport.street,
        transport.city,
        transport.postal_code
    ]
        .filter(Boolean)
        .join(", ");
    const clientName =
        transport.client_name || client?.name;

    useEffect(() => {
        if (
            !containerRef.current ||
            (!origin && !truck && !destination)
        ) {
            return;
        }

        const map = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false
        });

        L.tileLayer(
            "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            {maxZoom: 19}
        ).addTo(map);

        L.control
            .zoom({position: "bottomleft"})
            .addTo(map);

        const points = [];

        if (origin) {
            L.marker(origin, {
                icon: markerIcon("●", blueColor, 32)
            })
                .addTo(map)
                .bindTooltip(
                    markerCard(
                        t("origin"),
                        clientName || "—"
                    ),
                    {
                        permanent: true,
                        direction: "top",
                        offset: [0, -14],
                        className:
                            "transport-marker-card"
                    }
                );

            points.push(origin);
        }

        if (truck && isInTransit) {
            L.marker(truck, {
                icon: truckIcon()
            })
                .addTo(map)
                .bindTooltip(
                    markerCard(
                        t("truck"),
                        t("tracking")
                    ),
                    {
                        permanent: true,
                        direction: "top",
                        offset: [0, -28],
                        className:
                            "transport-marker-card"
                    }
                );

            points.push(truck);
        }

        const timeIcon = renderToStaticMarkup(
            <Icon
                icon="time"
                size={12}
                style={{
                    verticalAlign: "middle"
                }}
            />
        );

        if (destination) {
            L.marker(destination, {
                icon: destinationIcon()
            })
                .addTo(map)
                .bindTooltip(
                    markerCard(
                        t("destination"),
                        `
                            <b>${clientName || address || "—"}</b><br/>
                            <div style="display:flex; align-items:center; gap:5px; margin-top:4px;">
                                ${timeIcon}
                                <span>
                                    ${t("plannedArrival")}:<br/>
                                    ${transport.planned_arrival || "—"}
                                </span>
                            </div>
                        `
                    ),
                    {
                        permanent: true,
                        direction: "top",
                        offset: [-3, -48],
                        className:
                            "transport-marker-card"
                    }
                );

            points.push(destination);
        }

        if (points.length === 1) {
            map.setView(points[0], 11);
        }

        if (points.length === 2) {
            map.fitBounds(
                L.latLngBounds(points),
                {
                    padding: [70, 70],
                    maxZoom: 12
                }
            );
        }

        requestAnimationFrame(() =>
            map.invalidateSize()
        );

        return () => map.remove();
    }, [
        origin,
        truck,
        destination,
        isInTransit,
        address,
        transport.origin_name,
        transport.planned_arrival,
        t,
        clientName
    ]);

    return (
        <div
            style={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label={t("tracking")}
            onMouseDown={onClose}
        >
            <div
                style={styles.dialog}
                onMouseDown={event =>
                    event.stopPropagation()
                }
            >
                <div style={styles.mapArea}>
                    {origin ||
                    (truck && isInTransit) ||
                    destination ? (
                        <div
                            ref={containerRef}
                            style={styles.map}
                        />
                    ) : (
                        <div style={styles.emptyMap}>
                            {t("noCoordinates")}
                        </div>
                    )}

                    <div style={styles.titleCard}>
                        <div style={styles.titleIcon}>
                            <Icon
                                icon="truck"
                                size={26}
                            />
                        </div>

                        <div>
                            <strong>
                                {t("tracking")}
                            </strong>

                            <div
                                style={styles.muted}
                            >
                                {t("realTimeInfo")}
                            </div>
                        </div>
                    </div>
                </div>

                <aside style={styles.sidePanel}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={styles.closeButton}
                        aria-label={t("close")}
                    >
                        <Icon icon="cross" />
                    </button>

                    <h3 style={styles.panelTitle}>
                        {t("currentStatus")}
                    </h3>

                    <div
                        style={{
                            ...styles.statusBox,
                            borderColor: blueColor,
                            color: blueColor,
                            background: hasArrived
                                ? "#edf9ef"
                                : "#eef5ff"
                        }}
                    >
                        <div style={styles.statusLine}>
                            <span
                                style={{
                                    ...styles.dot,
                                    backgroundColor:
                                        blueColor
                                }}
                            />

                            {statusLabel(
                                transport.transport_status,
                                t
                            )}
                        </div>

                        <p
                            style={
                                styles.statusDescription
                            }
                        >
                            {hasNotStarted
                                ? t("notStartedDescription")
                                : hasArrived
                                    ? t("arrivedDescription")
                                    : isInTransit
                                        ? t("inTransitDescription")
                                        : null}
                        </p>
                    </div>

                    <Info
                        icon="truck"
                        label={t(
                            "transportationTableColumns.transport_code"
                        )}
                        value={
                            transport.transport_number
                        }
                    />

                    <Info
                        icon="calendar"
                        label={t(
                            "transportationTableColumns.departure_date"
                        )}
                        value={
                            transport.departure_date ||
                            "—"
                        }
                    />

                    <Info
                        icon="history"
                        label={t("lastUpdate")}
                        value={
                            transport.last_update ||
                            "—"
                        }
                    />

                    <Info
                        icon="person"
                        label={t("driver")}
                        value={
                            transport.carrier_name ||
                            "—"
                        }
                    />

                    <div style={styles.divider} />

                    <h4 style={styles.summaryTitle}>
                        {t("shipmentSummary")}
                    </h4>

                    {origin && (
                        <Info
                            icon="map-marker"
                            label={t("origin")}
                            value={
                                transport.origin_name ||
                                "—"
                            }
                            color={blueColor}
                        />
                    )}

                    {origin && (
                        <div
                            style={
                                styles.routeLine
                            }
                        />
                    )}

                    <Info
                        icon="map-marker"
                        label={t("destination")}
                        value={
                            <div>
                                <div>
                                    {clientName || "—"}
                                </div>
                                <div>
                                    {t("plannedArrival")}:{" "}
                                    {transport.planned_arrival ||
                                        "—"}
                                </div>
                            </div>
                        }
                        color={primaryColor}
                        transparentIcon
                    />

                    <div style={styles.notice}>
                        <Icon
                            icon="info-sign"
                            size={14}
                            color={blueColor}
                        />
                        <span>
                            {t("scheduleNotice")}
                        </span>
                    </div>
                </aside>
            </div>
        </div>
    );
};

const Info = ({
    icon,
    label,
    value,
    color,
    transparentIcon = false
}) => (
    <div style={styles.info}>
        <Icon
            icon={icon}
            style={{
                ...styles.infoIcon,
                ...(color && {color}),
                ...(transparentIcon && {
                    background: "transparent",
                    padding: 8
                })
            }}
        />

        <div>
            <strong style={styles.infoLabel}>
                {label}
            </strong>

            <div>{value}</div>
        </div>
    </div>
);

const validPoint = (lat, lng) =>
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng)) &&
    Number(lat) !== 0 &&
    Number(lng) !== 0
        ? [Number(lat), Number(lng)]
        : null;

const markerIcon = (symbol, color, size) =>
    L.divIcon({
        className: "",
        html: `
            <div style="
                width:${size}px;
                height:${size}px;
                border-radius:50%;
                display:flex;
                align-items:center;
                justify-content:center;
                background:${color};
                color:white;
                border:4px solid rgba(255,255,255,.9);
                box-shadow:
                    0 0 0 10px rgba(40,120,212,.18),
                    0 2px 9px rgba(0,0,0,.25);
                font-size:${size / 2}px
            ">
                ${symbol}
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [
            size / 2,
            size / 2
        ]
    });

const truckIcon = () => {
    const icon = renderToStaticMarkup(
        <Icon
            icon="truck"
            size={26}
            color="white"
        />
    );

    return L.divIcon({
        className: "",
        html: `
            <div style="
                width:52px;
                height:52px;
                border-radius:50%;
                display:flex;
                align-items:center;
                justify-content:center;
                background:${blueColor};
                color:white;
                border:4px solid rgba(255,255,255,.9);
                box-shadow:
                    0 0 0 10px rgba(40,120,212,.18),
                    0 2px 9px rgba(0,0,0,.25)
            ">
                ${icon}
            </div>
        `,
        iconSize: [52, 52],
        iconAnchor: [26, 26]
    });
};

const destinationIcon = () =>
    L.divIcon({
        className: "",
        html: `
            <div class="transport-destination-pin">
                <span></span>
            </div>
        `,
        iconSize: [34, 44],
        iconAnchor: [17, 44]
    });

const markerCard = (title, content) =>
    `<strong>${title}</strong><br/>${content}`;

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(18,30,45,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24
    },

    dialog: {
        width: "96vw",
        height: "92vh",
        position: "relative",
        background: "#edf3ed",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow:
            "0 22px 60px rgba(0,0,0,.3)"
    },

    mapArea: {
        position: "absolute",
        inset: 0,
        background: "#edf3ed"
    },

    map: {
        width: "100%",
        height: "100%"
    },

    emptyMap: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#64748b",
        padding: 40,
        textAlign: "center"
    },

    titleCard: {
        position: "absolute",
        zIndex: 500,
        top: 22,
        left: 22,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 18px",
        borderRadius: 12,
        background: "#fff",
        boxShadow:
            "0 3px 14px rgba(0,0,0,.16)",
        fontSize: 16
    },

    titleIcon: {
        color: blueColor,
        background: "#e9f2ff",
        borderRadius: "50%",
        padding: 10,
        width: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },

    muted: {
        fontSize: 12,
        color: "#718096",
        marginTop: 4
    },

    sidePanel: {
        width: 266,
        padding: "24px 22px 18px",
        overflowY: "auto",
        position: "absolute",
        zIndex: 600,
        top: 18,
        right: 18,
        bottom: 18,
        boxSizing: "border-box",
        borderRadius: 10,
        background: "#fff",
        boxShadow:
            "0 3px 14px rgba(0,0,0,.16)",
        color: "#243447"
    },

    closeButton: {
        position: "absolute",
        top: 12,
        right: 12,
        border: 0,
        background: "transparent",
        color: "#64748b",
        cursor: "pointer"
    },

    panelTitle: {
        margin: "0 0 14px",
        fontSize: 17,
        fontWeight: 700
    },

    statusBox: {
        display: "block",
        border: "1px solid",
        borderRadius: 8,
        padding: "13px 12px",
        fontWeight: "bold",
        background: "#fafcff",
        marginBottom: 22
    },

    statusLine: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        fontWeight: "bold"
    },

    statusDescription: {
        margin: "10px 0 0",
        padding: 0,
        color: "#64748b",
        fontSize: 12,
        lineHeight: 1.4,
        fontWeight: "normal"
    },

    dot: {
        width: 10,
        height: 10,
        borderRadius: "50%"
    },

    info: {
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        margin: "15px 0",
        fontSize: 13
    },

    infoIcon: {
        color: blueColor,
        background: "#edf5ff",
        padding: 8,
        borderRadius: 6,
        boxSizing: "content-box"
    },

    infoLabel: {
        display: "block",
        fontSize: 13,
        marginBottom: 3
    },

    divider: {
        borderTop: "1px solid #e5e7eb",
        margin: "24px 0 18px"
    },

    summaryTitle: {
        margin: "0 0 12px",
        fontSize: 15
    },

    routeLine: {
        height: 16,
        borderLeft: "2px dotted #b9c4d1",
        margin: "-8px 0 -8px 17px"
    },

    notice: {
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginTop: 18,
        padding: "10px 11px",
        border: "1px solid #b8d4ff",
        borderRadius: 6,
        background: "#f2f7ff",
        color: "#627084",
        fontSize: 12,
        lineHeight: 1.35
    }
};

export default TransportMap;